// Local-first lifecycle: init encrypted DB, run one-time seed, flush the outbox, and keep
// mirroring on reconnect. Native only; no-ops on web.
import { Capacitor } from "@capacitor/core";
import { initDb, getExec, SCHEMA_VERSION } from "./db";
import { seedFromServer, selfHealIfNeeded } from "./seed";
import { processOutbox } from "./sync";
import { configureGateway, setNetOnline, beginSession, reconcilePracticesFromServer } from "./gateway";
import { getCache } from "./repo";

let _booted = false;
let _onlineHandler = null;
let _resumeRemove = null;
let _net = null;
let _onSynced = null;

export function isNativeLocalFirst() { return Capacitor.isNativePlatform(); }

// Register a callback fired after an outbox flush that actually synced something, so the UI
// can invalidate its queries and refresh automatically on reconnect (no force-close needed).
export function setSyncListener(fn) { _onSynced = fn || null; }

async function runFlush(exec, net) {
  if (!exec || !net) return null;
  try {
    const r = await processOutbox(exec, net);
    if (r && r.synced > 0 && _onSynced) { try { _onSynced(r); } catch { /* ignore */ } }
    return r;
  } catch { return null; }
}

// Option B: authoritatively reconcile practices + their CURRENT agreements from the server so
// subsequent OFFLINE calculations use the current (not stale) agreement. Runs on app open and
// on reconnect while online. Offline/transient failures are swallowed — the offline calc then
// stays session-untrusted and shows Pending rather than a stale number.
async function reconcilePractices(net) {
  if (!net) return;
  try {
    const res = await net.get("/practices");
    if (res && Array.isArray(res.data)) await reconcilePracticesFromServer(res.data);
  } catch { /* offline or transient — leave untrusted for this session */ }
}

// Open the encrypted DB + wire the gateway EARLY (needs only the Keychain key, no network,
// no user). Lets a cold offline launch read the cached identity so the user isn't forced to
// Sign In. Also sets reliable connectivity and disables status-bar overlay (safe-area).
export async function bootstrapLocalDb() {
  if (!Capacitor.isNativePlatform()) return null;
  beginSession(); // mark the current app-session start for session-fresh agreement trust
  const exec = await initDb();
  configureGateway({ exec });
  try {
    const { StatusBar } = await import("@capacitor/status-bar");
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch { /* status-bar plugin optional */ }
  try {
    const { Network } = await import("@capacitor/network");
    const s = await Network.getStatus();
    setNetOnline(s.connected);
    Network.addListener("networkStatusChange", (st) => {
      setNetOnline(st.connected);
      if (st.connected) { const ex = getExec(); if (ex && _net) { runFlush(ex, _net); reconcilePractices(_net); } }
    });
  } catch { /* network plugin optional */ }
  return exec;
}

// Called once the user is authenticated. `net` is the raw (non-gateway) axios instance.
export async function initLocalFirst(user, net) {
  if (!Capacitor.isNativePlatform() || !user || !user.id) return { skipped: true };
  _net = net;
  const exec = await initDb();
  configureGateway({ net, exec, userId: user.id });

  let seed = { skipped: "offline" };
  try {
    seed = await seedFromServer(exec, net, user.id); // idempotent; only runs when not yet seeded
    // Online self-heal: if already seeded but the DB is empty/incomplete while the server has
    // records, restore them (download + upsert only). No-op when offline or the account is
    // genuinely empty. Original emptying trigger remains unconfirmed; this recovers from it.
    if (seed && seed.alreadySeeded) {
      try { seed = { ...seed, selfHeal: await selfHealIfNeeded(exec, net, user.id) }; }
      catch (e2) { seed = { ...seed, selfHealError: String(e2 && e2.message) }; }
    }
  } catch (e) {
    // Leave migration incomplete so it is retried; never present an empty DB as success.
    seed = { error: String(e && e.message) };
  }

  const flush = () => { const ex = getExec(); if (ex) runFlush(ex, net); };
  flush();
  reconcilePractices(net); // Option B: pull current agreements on app open (online)

  if (!_booted) {
    _onlineHandler = () => { flush(); reconcilePractices(net); };
    if (typeof window !== "undefined") window.addEventListener("online", _onlineHandler);
    try {
      const { App } = await import("@capacitor/app");
      const h = await App.addListener("resume", () => { flush(); reconcilePractices(net); });
      _resumeRemove = () => h.remove();
    } catch { /* @capacitor/app optional */ }
    _booted = true;
  }
  return { schemaVersion: SCHEMA_VERSION, seed };
}

export function teardownLocalFirst() {
  configureGateway({ userId: null });
}

export function triggerSync() {
  const ex = getExec();
  if (ex && _net) return runFlush(ex, _net);
  return Promise.resolve({ skipped: "not-ready" });
}

// Locally cached identity (from the last successful /auth/me) — lets an authenticated
// native user stay signed in when the backend is unreachable at launch.
export async function getLocalMe() {
  const ex = getExec();
  if (!ex) return null;
  try { const v = await getCache(ex, "/auth/me"); return v && v.id ? v : null; } catch { return null; }
}
